import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getEmailStatus,
  sendBulkEmail,
  previewEmail,
  BulkRecipient,
  BulkSendResult,
  BULK_BATCH_SIZE,
} from '../services/email';
import {
  parseRecipientSheet,
  downloadTemplateSheet,
  renderTemplate,
  ParsedSheet,
  ParsedRecipient,
} from '../utils/recipientSheet';
import AnimatedSection from './AnimatedSection';
import { useAuth } from '../context/AuthContext';

const CAMPAIGN_KEY = 'nexa_bulk_campaign_draft';

const DEFAULT_GREETING = 'Hi Team,';

const DEFAULT_SUBJECT = 'An all-in-one system for {{name}}';

const DEFAULT_MESSAGE = `I am {{sender}} with Nexasolutra, the team behind Zoxohu.

We built Zoxohu to solve a major problem for restaurants which is having your operations scattered across different spreadsheets and tablets. We designed it to be the only software a restaurant needs to operate. As we call it, the restaurant operating system.

Instead of using different tools for online orders (like Chowdeck and Glovo), a bulky terminal for your POS, manual menu, and spreadsheets for inventory, Zoxohu handles it all in one easy-to-use platform:

• Hardware-free POS & Kitchen Displays
• QR code table ordering
• Chowdeck and Glovo integration: manage all third-party delivery orders directly from our platform
• Inventory & multi-branch management
• A zero-commission online storefront

We are currently looking for a few great brands like {{name}} to onboard. If you are open to exploring a more efficient way to run your operations, we would love to connect.

Let me know if you have time next week for a quick intro and demo.`;

const DEFAULT_SIGN_OFF = 'Best regards,';

const TOKENS = [
  { token: '{{name}}', label: 'Restaurant name' },
  { token: '{{email}}', label: 'Email' },
  { token: '{{phone}}', label: 'Phone' },
];

const loadCampaign = () => {
  try {
    const raw = localStorage.getItem(CAMPAIGN_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      return {
        greeting: saved.greeting ?? DEFAULT_GREETING,
        subjectTemplate: saved.subjectTemplate ?? DEFAULT_SUBJECT,
        messageTemplate: saved.messageTemplate ?? DEFAULT_MESSAGE,
        signOff: saved.signOff ?? DEFAULT_SIGN_OFF,
      };
    }
  } catch {
    // ignore
  }
  return {
    greeting: DEFAULT_GREETING,
    subjectTemplate: DEFAULT_SUBJECT,
    messageTemplate: DEFAULT_MESSAGE,
    signOff: DEFAULT_SIGN_OFF,
  };
};

/** Saves text to a file the browser downloads. */
const saveFile = (filename: string, contents: string, mime: string): void => {
  const url = URL.createObjectURL(new Blob([contents], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/** Turns a campaign subject into a safe filename stem. */
const slugify = (value: string): string =>
  value
    .replace(/\{\{.*?\}\}/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'nexasolutra-email';

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

const StepBadge: React.FC<{ n: number; title: string; hint?: string }> = ({ n, title, hint }) => (
  <div className="flex items-start gap-3 mb-5">
    <span className="shrink-0 w-7 h-7 rounded-full bg-primary-500/15 border border-primary-500/40 text-primary-400 text-sm font-semibold flex items-center justify-center">
      {n}
    </span>
    <div>
      <h2 className="text-white font-medium leading-7">{title}</h2>
      {hint && <p className="text-sm text-muted mt-0.5">{hint}</p>}
    </div>
  </div>
);

const BulkEmailSender: React.FC = () => {
  const { admin } = useAuth();
  const savedCampaign = loadCampaign();

  // Sending identity comes from the admin's saved account details.
  const zohoEmail = admin?.zohoEmail || admin?.email || '';
  const senderName = admin?.name || '';
  const senderTitle = admin?.title || '';
  const senderPhone = admin?.phone || '';
  const hasSavedPassword = Boolean(admin?.hasSavedMailPassword);

  // Only needed when nothing is saved on the server yet.
  const [zohoPassword, setZohoPassword] = useState('');

  const [sheet, setSheet] = useState<ParsedSheet | null>(null);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState(false);

  const [greeting, setGreeting] = useState(savedCampaign.greeting);
  const [subjectTemplate, setSubjectTemplate] = useState(savedCampaign.subjectTemplate);
  const [messageTemplate, setMessageTemplate] = useState(savedCampaign.messageTemplate);
  const [signOff, setSignOff] = useState(savedCampaign.signOff);
  const [delayMs, setDelayMs] = useState(300);

  const [previewIndex, setPreviewIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<BulkSendResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [smtpHost, setSmtpHost] = useState('smtppro.zoho.com:465');

  const stopRef = useRef(false);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getEmailStatus()
      .then((data) => setSmtpHost(`${data.host}:${data.port}`))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    localStorage.setItem(
      CAMPAIGN_KEY,
      JSON.stringify({ greeting, subjectTemplate, messageTemplate, signOff })
    );
  }, [greeting, subjectTemplate, messageTemplate, signOff]);

  const allRecipients = useMemo(() => sheet?.recipients ?? [], [sheet]);
  const invalidRecipients = useMemo(
    () => allRecipients.filter((r) => !r.valid),
    [allRecipients]
  );
  const selected = useMemo(
    () => allRecipients.filter((r) => r.valid && !excluded.has(r.email)),
    [allRecipients, excluded]
  );

  const previewRecipient: ParsedRecipient | undefined =
    selected[previewIndex] ?? selected[0];

  /** {{sender}} is filled locally from the sender-name field, not from the sheet. */
  const withSender = (text: string) =>
    text.replace(/\{\{\s*sender\s*\}\}/gi, senderName || 'Precious');

  const previewSubject = previewRecipient
    ? renderTemplate(withSender(subjectTemplate), previewRecipient)
    : withSender(subjectTemplate);
  const previewGreeting = previewRecipient
    ? renderTemplate(withSender(greeting), previewRecipient)
    : withSender(greeting);
  const previewBody = previewRecipient
    ? renderTemplate(withSender(messageTemplate), previewRecipient)
    : withSender(messageTemplate);

  const handleFile = async (file: File) => {
    setError(null);
    setNotice(null);
    setResults([]);
    setParsing(true);
    try {
      const parsed = await parseRecipientSheet(file);
      setSheet(parsed);
      setFileName(file.name);
      setExcluded(new Set());
      setPreviewIndex(0);
      const validCount = parsed.recipients.filter((r) => r.valid).length;
      setNotice(
        `Loaded ${validCount} valid recipient${validCount === 1 ? '' : 's'} from "${parsed.sheetName}".`
      );
    } catch (err) {
      setSheet(null);
      setFileName('');
      setError(err instanceof Error ? err.message : 'Could not read that file.');
    } finally {
      setParsing(false);
    }
  };

  const toggleRecipient = (email: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const insertToken = (token: string) => {
    const el = messageRef.current;
    if (!el) {
      setMessageTemplate((m: string) => `${m}${token}`);
      return;
    }
    const start = el.selectionStart ?? messageTemplate.length;
    const end = el.selectionEnd ?? start;
    const next = messageTemplate.slice(0, start) + token + messageTemplate.slice(end);
    setMessageTemplate(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  /** Sends a list in batches, streaming results in as each batch returns. */
  const runSend = async (recipients: BulkRecipient[]) => {
    stopRef.current = false;
    setSending(true);
    setError(null);
    setNotice(null);
    setResults([]);
    setProgress({ done: 0, total: recipients.length });

    const batches = chunk(recipients, BULK_BATCH_SIZE);
    let done = 0;

    for (const batch of batches) {
      if (stopRef.current) {
        setNotice(`Stopped. ${done} of ${recipients.length} emails were sent.`);
        break;
      }

      try {
        const response = await sendBulkEmail({
          // Omitted when a password is saved server-side, so it is never
          // round-tripped through the browser.
          ...(zohoPassword ? { zohoEmail, zohoPassword } : {}),
          recipients: batch,
          subjectTemplate: withSender(subjectTemplate).trim(),
          messageTemplate: withSender(messageTemplate).trim(),
          greeting: withSender(greeting).trim() || undefined,
          signOff: signOff.trim() || undefined,
          senderName: senderName.trim() || undefined,
          senderTitle: senderTitle.trim() || undefined,
          senderPhone: senderPhone.trim() || undefined,
          delayMs,
        });
        setResults((prev) => [...prev, ...response.results]);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Batch failed';
        setResults((prev) => [
          ...prev,
          ...batch.map((r) => ({ ...r, success: false, error: message })),
        ]);

        // A bad password will fail every remaining batch the same way — stop now.
        if (/login failed|password|EAUTH/i.test(message)) {
          setError(`${message} Sending stopped.`);
          done += batch.length;
          setProgress({ done, total: recipients.length });
          break;
        }
      }

      done += batch.length;
      setProgress({ done, total: recipients.length });
    }

    setSending(false);
  };

  const validateBeforeSend = (): string | null => {
    if (!zohoEmail) return 'No Zoho mailbox on your account. Set one in Account.';
    if (!hasSavedPassword && !zohoPassword) {
      return 'Enter your Zoho password, or save it once in Account settings.';
    }
    if (!sheet) return 'Upload a recipient list first.';
    if (selected.length === 0) return 'No valid recipients selected.';
    if (!subjectTemplate.trim()) return 'The subject line is required.';
    if (!messageTemplate.trim()) return 'The message body is required.';
    return null;
  };

  const handleReview = () => {
    const problem = validateBeforeSend();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setConfirming(true);
  };

  const handleConfirmedSend = () => {
    setConfirming(false);
    runSend(
      selected.map((r) => ({ email: r.email, name: r.name, phone: r.phone }))
    );
  };

  const failed = results.filter((r) => !r.success);
  const sentCount = results.filter((r) => r.success).length;

  const handleRetryFailed = () => {
    if (failed.length === 0) return;
    runSend(failed.map((r) => ({ email: r.email, name: r.name })));
  };

  const downloadReport = () => {
    const rows = [
      ['restaurantName', 'email', 'status', 'detail'],
      ...results.map((r) => [
        r.name || '',
        r.email,
        r.success ? 'sent' : 'failed',
        r.success ? r.messageId || '' : r.error || '',
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    saveFile('bulk-email-report.csv', csv, 'text/csv;charset=utf-8;');
  };

  /**
   * Downloads the finished email as .html and .txt, rendered by the server so
   * the file matches what recipients actually receive — signature included.
   * Uses the currently previewed restaurant, or a sample when none is loaded.
   */
  const downloadRenderedEmail = async () => {
    setError(null);
    setNotice(null);
    try {
      setDownloading(true);
      const rendered = await previewEmail({
        subjectTemplate: withSender(subjectTemplate),
        messageTemplate: withSender(messageTemplate),
        greeting: withSender(greeting) || undefined,
        signOff: signOff || undefined,
        senderName: senderName || undefined,
        senderTitle: senderTitle || undefined,
        senderPhone: senderPhone || undefined,
        recipient: previewRecipient
          ? {
              email: previewRecipient.email,
              name: previewRecipient.name,
              phone: previewRecipient.phone,
            }
          : undefined,
      });

      const stem = slugify(rendered.subject);
      saveFile(`${stem}.html`, rendered.html, 'text/html;charset=utf-8;');
      saveFile(`${stem}.txt`, rendered.text, 'text/plain;charset=utf-8;');

      setNotice(
        `Downloaded the email for ${rendered.recipient.name || rendered.recipient.email} as .html and .txt.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not render the email');
    } finally {
      setDownloading(false);
    }
  };

  /** Saves the editable copy so a campaign can be reused or shared. */
  const downloadTemplateFile = () => {
    const campaign = {
      nexasolutraTemplate: 1,
      subject: subjectTemplate,
      greeting,
      body: messageTemplate,
      signOff,
    };
    saveFile(
      `${slugify(subjectTemplate)}-template.json`,
      JSON.stringify(campaign, null, 2),
      'application/json'
    );
  };

  const loadTemplateFile = async (file: File) => {
    setError(null);
    setNotice(null);
    try {
      const parsed = JSON.parse(await file.text());

      if (!parsed || typeof parsed.body !== 'string' || typeof parsed.subject !== 'string') {
        throw new Error('That file is not a saved email template.');
      }

      setSubjectTemplate(parsed.subject);
      setMessageTemplate(parsed.body);
      if (typeof parsed.greeting === 'string') setGreeting(parsed.greeting);
      if (typeof parsed.signOff === 'string') setSignOff(parsed.signOff);
      setNotice(`Loaded the template from "${file.name}".`);
    } catch (err) {
      setError(
        err instanceof Error && err.message.includes('not a saved')
          ? err.message
          : 'Could not read that template file.'
      );
    }
  };

  const inputClass =
    'w-full rounded-lg border border-surface-border bg-surface px-4 py-3 text-white placeholder:text-muted/50 focus:outline-none focus:border-primary-500';

  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection animation="slide-up">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-white mb-2">
                Bulk Email
              </h1>
              <p className="text-muted">
                Upload a spreadsheet and send one personalised email per restaurant.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/email"
                className="text-sm px-3 py-2 rounded-lg border border-surface-border text-muted hover:text-white hover:border-primary-500/40 transition-colors"
              >
                Single email
              </Link>
              <div className="text-sm px-3 py-2 rounded-lg border border-surface-border text-muted">
                SMTP · {smtpHost}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {error && (
          <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="mb-6 rounded-lg border border-primary-500/40 bg-primary-500/10 text-primary-400 px-4 py-3 text-sm">
            {notice}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Left column — the campaign builder */}
          <div className="lg:col-span-3 space-y-6">
            {/* Step 1 — account */}
            <AnimatedSection animation="slide-up">
              <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8">
                <StepBadge
                  n={1}
                  title="Sending as"
                  hint="Pulled from your account — change it once in Account settings."
                />

                <div className="rounded-xl border border-surface-border bg-surface p-4 flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-white">{senderName || 'Your name'}</p>
                    <p className="text-sm text-muted">
                      {senderTitle || 'No title set'} · Nexasolutra
                    </p>
                    <p className="text-sm text-primary-400 mt-1 truncate">{zohoEmail}</p>
                    {senderPhone && <p className="text-sm text-muted">{senderPhone}</p>}
                  </div>
                  <Link
                    to="/admin/account"
                    className="text-sm px-3 py-2 rounded-lg border border-surface-border text-muted hover:text-white hover:border-primary-500/40 transition-colors"
                  >
                    Edit details
                  </Link>
                </div>

                {hasSavedPassword ? (
                  <p className="text-sm text-primary-400 mt-4">
                    ✓ Mail password saved — you will not be asked for it.
                  </p>
                ) : (
                  <div className="mt-4">
                    <label className="block text-sm text-muted mb-2">Zoho password *</label>
                    <input
                      type="password"
                      value={zohoPassword}
                      onChange={(e) => setZohoPassword(e.target.value)}
                      placeholder="Password or app password"
                      autoComplete="current-password"
                      className={inputClass}
                    />
                    <p className="text-xs text-muted mt-2">
                      Needed for this send only.{' '}
                      <Link to="/admin/account" className="text-primary-500 hover:text-primary-400">
                        Save it in Account
                      </Link>{' '}
                      to stop retyping it.
                    </p>
                  </div>
                )}
              </div>
            </AnimatedSection>

            {/* Step 2 — recipient list */}
            <AnimatedSection animation="slide-up" delay={60}>
              <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8">
                <StepBadge
                  n={2}
                  title="Recipient list"
                  hint="An .xlsx, .xls or .csv file with restaurantName, phoneNumber and email columns."
                />

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFile(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`rounded-xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
                    dragging
                      ? 'border-primary-500 bg-primary-500/5'
                      : 'border-surface-border hover:border-primary-500/50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFile(file);
                      e.target.value = '';
                    }}
                  />
                  <svg
                    className="w-9 h-9 mx-auto mb-3 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.9A5 5 0 1115.9 6H16a4 4 0 010 8h-1m-4-3v8m0-8l-3 3m3-3l3 3"
                    />
                  </svg>
                  <p className="text-white text-sm font-medium">
                    {parsing ? 'Reading file…' : fileName || 'Drop your spreadsheet here, or click to browse'}
                  </p>
                  <p className="text-xs text-muted mt-1">.xlsx, .xls or .csv — the first sheet is used</p>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      downloadTemplateSheet().catch(() =>
                        setError('Could not build the template file.')
                      );
                    }}
                    className="text-sm text-primary-500 hover:text-primary-400"
                  >
                    ↓ Download a blank template
                  </button>
                  {sheet && (
                    <button
                      type="button"
                      onClick={() => {
                        setSheet(null);
                        setFileName('');
                        setExcluded(new Set());
                        setResults([]);
                        setNotice(null);
                      }}
                      className="text-sm text-muted hover:text-white"
                    >
                      Clear list
                    </button>
                  )}
                </div>

                {sheet && (
                  <div className="mt-6 space-y-4">
                    {/* Column mapping */}
                    <div className="rounded-xl border border-surface-border bg-surface p-4 text-sm">
                      <p className="text-muted mb-2">Detected columns</p>
                      <div className="flex flex-wrap gap-2">
                        {(['name', 'email', 'phone'] as const).map((field) => (
                          <span
                            key={field}
                            className={`px-2.5 py-1 rounded-md text-xs border ${
                              sheet.matched[field]
                                ? 'border-primary-500/40 bg-primary-500/10 text-primary-400'
                                : 'border-surface-border text-muted'
                            }`}
                          >
                            {field} → {sheet.matched[field] || 'not found'}
                          </span>
                        ))}
                      </div>
                      {!sheet.matched.name && (
                        <p className="text-amber-400/90 text-xs mt-3">
                          No restaurant-name column found, so {'{{name}}'} will come out blank. Rename a
                          column to "restaurantName".
                        </p>
                      )}
                    </div>

                    {/* Counts */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-xl border border-surface-border bg-surface p-4">
                        <p className="text-2xl font-semibold text-white">{selected.length}</p>
                        <p className="text-xs text-muted mt-1">Will receive</p>
                      </div>
                      <div className="rounded-xl border border-surface-border bg-surface p-4">
                        <p className="text-2xl font-semibold text-white">{excluded.size}</p>
                        <p className="text-xs text-muted mt-1">Unchecked</p>
                      </div>
                      <div className="rounded-xl border border-surface-border bg-surface p-4">
                        <p className="text-2xl font-semibold text-amber-400">{invalidRecipients.length}</p>
                        <p className="text-xs text-muted mt-1">Skipped rows</p>
                      </div>
                    </div>

                    {/* Recipient table */}
                    <div className="rounded-xl border border-surface-border overflow-hidden">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-surface-hover">
                            <tr>
                              <th className="w-10 py-2.5 px-3" />
                              <th className="text-left py-2.5 px-3 font-medium text-white">Restaurant</th>
                              <th className="text-left py-2.5 px-3 font-medium text-white">Email</th>
                              <th className="text-left py-2.5 px-3 font-medium text-white">Phone</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allRecipients.map((r) => (
                              <tr
                                key={`${r.row}-${r.email}`}
                                className={`border-t border-surface-border ${
                                  r.valid ? '' : 'opacity-50'
                                }`}
                              >
                                <td className="py-2 px-3">
                                  <input
                                    type="checkbox"
                                    disabled={!r.valid}
                                    checked={r.valid && !excluded.has(r.email)}
                                    onChange={() => toggleRecipient(r.email)}
                                    className="accent-primary-500 w-4 h-4"
                                  />
                                </td>
                                <td className="py-2 px-3 text-white">{r.name || '—'}</td>
                                <td className="py-2 px-3 text-muted">
                                  {r.email || <span className="italic">blank</span>}
                                </td>
                                <td className="py-2 px-3 text-muted">
                                  {r.issue ? (
                                    <span className="text-amber-400 text-xs">
                                      Row {r.row}: {r.issue}
                                    </span>
                                  ) : (
                                    r.phone || '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </AnimatedSection>

            {/* Step 3 — message */}
            <AnimatedSection animation="slide-up" delay={120}>
              <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8 space-y-5">
                <StepBadge
                  n={3}
                  title="Message template"
                  hint="Placeholders are swapped per recipient before each email goes out."
                />

                <div className="flex flex-wrap gap-2">
                  {TOKENS.map((t) => (
                    <button
                      key={t.token}
                      type="button"
                      onClick={() => insertToken(t.token)}
                      title={`Insert ${t.label}`}
                      className="px-2.5 py-1 rounded-md text-xs font-mono border border-primary-500/40 bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors"
                    >
                      {t.token}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setGreeting(DEFAULT_GREETING);
                      setSubjectTemplate(DEFAULT_SUBJECT);
                      setMessageTemplate(DEFAULT_MESSAGE);
                      setSignOff(DEFAULT_SIGN_OFF);
                    }}
                    className="px-2.5 py-1 rounded-md text-xs border border-surface-border text-muted hover:text-white ml-auto"
                  >
                    Reset to Zoxohu template
                  </button>
                </div>

                <div>
                  <label className="block text-sm text-muted mb-2">Subject *</label>
                  <input
                    type="text"
                    value={subjectTemplate}
                    onChange={(e) => setSubjectTemplate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted mb-2">Opening line</label>
                    <input
                      type="text"
                      value={greeting}
                      onChange={(e) => setGreeting(e.target.value)}
                      placeholder="Hi Team,"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted mb-2">Sign-off</label>
                    <input
                      type="text"
                      value={signOff}
                      onChange={(e) => setSignOff(e.target.value)}
                      placeholder="Best regards,"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-muted mb-2">Body *</label>
                  <textarea
                    ref={messageRef}
                    value={messageTemplate}
                    onChange={(e) => setMessageTemplate(e.target.value)}
                    rows={16}
                    className={`${inputClass} resize-y font-sans leading-relaxed`}
                  />
                  <p className="text-xs text-muted mt-2">
                    Your name, title and the Nexasolutra signature block are added automatically after
                    the sign-off — don't repeat them here.
                  </p>
                </div>

                {/* Template files */}
                <div className="rounded-xl border border-surface-border bg-surface p-4">
                  <p className="text-xs uppercase tracking-widest text-muted mb-3">Template file</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={downloadRenderedEmail}
                      disabled={downloading}
                      className="px-3 py-1.5 rounded-md text-xs border border-primary-500/40 bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors disabled:opacity-50"
                    >
                      {downloading ? 'Rendering…' : '↓ Download email (.html + .txt)'}
                    </button>
                    <button
                      type="button"
                      onClick={downloadTemplateFile}
                      className="px-3 py-1.5 rounded-md text-xs border border-surface-border text-muted hover:text-white transition-colors"
                    >
                      ↓ Save template (.json)
                    </button>
                    <button
                      type="button"
                      onClick={() => templateInputRef.current?.click()}
                      className="px-3 py-1.5 rounded-md text-xs border border-surface-border text-muted hover:text-white transition-colors"
                    >
                      ↑ Load template (.json)
                    </button>
                    <input
                      ref={templateInputRef}
                      type="file"
                      accept=".json,application/json"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) loadTemplateFile(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted mt-3">
                    The .html download is rendered by the server, so it is exactly the email
                    {previewRecipient ? ` ${previewRecipient.name || previewRecipient.email}` : ' a recipient'}{' '}
                    receives — signature and all. The .json keeps the editable copy so you can reuse
                    or share this campaign.
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-muted mb-2">Pause between emails</label>
                  <select
                    value={delayMs}
                    onChange={(e) => setDelayMs(Number(e.target.value))}
                    className={inputClass}
                  >
                    <option value={300}>0.3s — recommended</option>
                    <option value={800}>0.8s — gentle</option>
                    <option value={1500}>1.5s — very gentle</option>
                  </select>
                  <p className="text-xs text-muted mt-2">
                    Spacing sends out helps keep Zoho from rate-limiting or flagging the batch as spam.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            {/* Step 4 — send */}
            <AnimatedSection animation="slide-up" delay={180}>
              <div className="rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8">
                <StepBadge n={4} title="Send" hint="Emails go out one at a time, each addressed only to its own recipient." />

                {!sending && !confirming && results.length === 0 && (
                  <button type="button" onClick={handleReview} className="btn-primary w-full sm:w-auto">
                    Review &amp; send to {selected.length} recipient{selected.length === 1 ? '' : 's'}
                  </button>
                )}

                {confirming && (
                  <div className="rounded-xl border border-primary-500/40 bg-primary-500/5 p-5">
                    <p className="text-white font-medium mb-2">
                      Send {selected.length} email{selected.length === 1 ? '' : 's'} from {zohoEmail}?
                    </p>
                    <p className="text-sm text-muted mb-1">
                      First recipient: {selected[0]?.name || selected[0]?.email} — "{previewSubject}"
                    </p>
                    <p className="text-sm text-muted mb-4">
                      This sends real email to real people and cannot be undone.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button type="button" onClick={handleConfirmedSend} className="btn-primary">
                        Yes, send {selected.length} email{selected.length === 1 ? '' : 's'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(false)}
                        className="btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {(sending || results.length > 0) && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white">
                          {sending ? 'Sending…' : 'Finished'} {progress.done} / {progress.total}
                        </span>
                        <span className="text-muted">{percent}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-surface overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-500"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="text-primary-400">{sentCount} sent</span>
                      {failed.length > 0 && <span className="text-red-400">{failed.length} failed</span>}
                    </div>

                    {sending && (
                      <button
                        type="button"
                        onClick={() => {
                          stopRef.current = true;
                        }}
                        className="btn-secondary"
                      >
                        Stop after this batch
                      </button>
                    )}

                    {!sending && results.length > 0 && (
                      <div className="flex flex-wrap gap-3">
                        {failed.length > 0 && (
                          <button type="button" onClick={handleRetryFailed} className="btn-primary">
                            Retry {failed.length} failed
                          </button>
                        )}
                        <button type="button" onClick={downloadReport} className="btn-secondary">
                          Download report (CSV)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setResults([]);
                            setProgress({ done: 0, total: 0 });
                          }}
                          className="btn-ghost"
                        >
                          Start over
                        </button>
                      </div>
                    )}

                    {results.length > 0 && (
                      <div className="rounded-xl border border-surface-border overflow-hidden">
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <tbody>
                              {results.map((r, i) => (
                                <tr key={`${r.email}-${i}`} className="border-t border-surface-border first:border-t-0">
                                  <td className="py-2 px-3 w-6">
                                    <span
                                      className={`inline-block w-2 h-2 rounded-full ${
                                        r.success ? 'bg-primary-500' : 'bg-red-500'
                                      }`}
                                    />
                                  </td>
                                  <td className="py-2 px-3 text-white">{r.name || '—'}</td>
                                  <td className="py-2 px-3 text-muted">{r.email}</td>
                                  <td className="py-2 px-3 text-xs text-red-400">{r.error || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AnimatedSection>
          </div>

          {/* Right column — live preview */}
          <AnimatedSection animation="slide-up" delay={100} className="lg:col-span-2">
            <div className="sticky top-24 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs uppercase tracking-widest text-muted">Preview</p>
                {selected.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                      disabled={previewIndex === 0}
                      className="px-2 py-1 rounded border border-surface-border text-muted hover:text-white disabled:opacity-40"
                    >
                      ←
                    </button>
                    <span className="text-xs text-muted">
                      {previewIndex + 1} / {selected.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPreviewIndex((i) => Math.min(selected.length - 1, i + 1))}
                      disabled={previewIndex >= selected.length - 1}
                      className="px-2 py-1 rounded border border-surface-border text-muted hover:text-white disabled:opacity-40"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-surface-border bg-white overflow-hidden">
                {/* Envelope header */}
                <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
                  <p className="text-[11px] text-zinc-500">
                    To: {previewRecipient?.email || 'no recipient loaded'}
                  </p>
                  <p className="text-sm font-semibold text-zinc-900 mt-1 break-words">
                    {previewSubject || 'Subject line'}
                  </p>
                </div>

                <div className="p-5 max-h-[520px] overflow-y-auto">
                  <p className="text-[13px] leading-relaxed text-zinc-700 mb-4">{previewGreeting}</p>
                  {previewBody.split(/\n{2,}/).map((paragraph, i) => (
                    <p key={i} className="text-[13px] leading-relaxed text-zinc-700 mb-4 whitespace-pre-line">
                      {paragraph}
                    </p>
                  ))}
                  <p className="text-[13px] leading-relaxed text-zinc-700 mt-5 mb-0">{signOff}</p>
                  <p className="text-[13px] leading-relaxed text-zinc-700">{senderName || 'Your Name'}</p>

                  {/* Signature block — mirrors the server template */}
                  <div className="flex gap-4 items-start border-t border-zinc-200 pt-4 mt-6">
                    <div className="shrink-0 w-[120px]">
                      <img src="/nexalogo.png" alt="Nexasolutra" className="w-10 h-10 mb-2 object-contain" />
                      <p className="font-semibold text-zinc-900 text-base leading-tight">Nexasolutra</p>
                      <p className="text-[9px] font-semibold text-primary-600 uppercase tracking-wide leading-snug mt-1.5">
                        AI Operating Systems for Hospitality & Commerce
                      </p>
                    </div>

                    <div className="w-0.5 self-stretch bg-primary-500 shrink-0" />

                    <div className="min-w-0 pt-0.5">
                      <p className="font-serif font-bold text-zinc-900 text-lg leading-tight">
                        {senderName || 'Your Name'}
                      </p>
                      <p className="font-serif text-sm text-zinc-600 mt-0.5">{senderTitle || 'Your Title'}</p>
                      <p className="font-serif text-sm text-zinc-600 mb-3">Nexasolutra</p>
                      <p className="text-xs text-blue-600 mb-1 truncate">
                        {zohoEmail || 'you@nexasolutra.com'}
                      </p>
                      {senderPhone && <p className="text-xs text-zinc-800">{senderPhone}</p>}
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-zinc-200 space-y-2 text-xs text-zinc-600">
                    <p>Flt 7 by Chief/Pal, Along Gbuduwyi Kabusa, Abuja</p>
                    <p>Nexasolutra</p>
                    <p className="text-blue-600">www.nexasolutra.com</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted">
                Each recipient gets their own email — no one sees the rest of the list.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default BulkEmailSender;
